import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ChevronDown, ChevronUp } from 'lucide-react';

// All languages supported
const ALL_SUPPORTED_LANGUAGES = {
  'sv': { name: 'Svenska', flag: '🇸🇪' },
  'en': { name: 'English', flag: '🇬🇧' },
  'pl': { name: 'Polski', flag: '🇵🇱' },
  'es': { name: 'Español', flag: '🇪🇸' },
  'ar': { name: 'العربية', flag: '🇱🇧' },
  'de': { name: 'Deutsch', flag: '🇩🇪' },
  'fr': { name: 'Français', flag: '🇫🇷' },
  'it': { name: 'Italiano', flag: '🇮🇹' },
  'pt': { name: 'Português', flag: '🇵🇹' },
  'nl': { name: 'Nederlands', flag: '🇳🇱' },
  'ru': { name: 'Русский', flag: '🇷🇺' },
  'uk': { name: 'Українська', flag: '🇺🇦' },
  'hr': { name: 'Hrvatski', flag: '🇭🇷' },
  'cs': { name: 'Čeština', flag: '🇨🇿' },
  'sk': { name: 'Slovenčina', flag: '🇸🇰' },
  'hu': { name: 'Magyar', flag: '🇭🇺' },
  'ro': { name: 'Română', flag: '🇷🇴' },
  'bg': { name: 'Български', flag: '🇧🇬' },
  'el': { name: 'Ελληνικά', flag: '🇬🇷' },
  'tr': { name: 'Türkçe', flag: '🇹🇷' },
  'fi': { name: 'Suomi', flag: '🇫🇮' },
  'da': { name: 'Dansk', flag: '🇩🇰' },
  'no': { name: 'Norsk', flag: '🇳🇴' },
  'zh': { name: '中文', flag: '🇨🇳' },
  'ja': { name: '日本語', flag: '🇯🇵' },
  'ko': { name: '한국어', flag: '🇰🇷' },
  'vi': { name: 'Tiếng Việt', flag: '🇻🇳' },
  'th': { name: 'ไทย', flag: '🇹🇭' },
  'hi': { name: 'हिन्दी', flag: '🇮🇳' },
  'he': { name: 'עברית', flag: '🇮🇱' },
  'tl': { name: 'Filipino', flag: '🇵🇭' },
  'id': { name: 'Bahasa Indonesia', flag: '🇮🇩' },
  'ms': { name: 'Bahasa Melayu', flag: '🇲🇾' },
  'sw': { name: 'Kiswahili', flag: '🇰🇪' },
  'la': { name: 'Latina', flag: '🇻🇦' },
};

const FloatingLanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { language, changeLanguage, settings, getAvailableLanguages, getCleanPath } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle language change with URL update
  const handleLanguageChange = (newLang) => {
    const cleanPath = getCleanPath(location.pathname);
    changeLanguage(newLang);
    
    // Build new path with language prefix
    if (newLang === settings.default_language) {
      navigate(cleanPath || '/', { replace: true });
    } else {
      navigate(`/${newLang}${cleanPath}`, { replace: true });
    }
    setIsOpen(false);
  };

  // Get enabled languages from settings
  const availableLangs = getAvailableLanguages();
  const languages = availableLangs.map(code => ({
    code,
    name: ALL_SUPPORTED_LANGUAGES[code]?.name || code.toUpperCase(),
    flag: ALL_SUPPORTED_LANGUAGES[code]?.flag || '🌐',
  }));

  // Hide if translation is disabled or only one language
  const showSelector = settings.translation_enabled && languages.length > 1;
  if (!showSelector) return null;

  // Get current language info
  const currentLang = ALL_SUPPORTED_LANGUAGES[language] || { name: language.toUpperCase(), flag: '🌐' };

  return (
    <div 
      ref={dropdownRef}
      className="fixed top-20 right-4 z-40"
      data-testid="floating-language-selector"
    >
      {/* Toggle Button - Compact design */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-gray-50"
        aria-label="Select language"
        data-testid="language-selector-toggle"
      >
        <span className="text-lg leading-none">{currentLang.flag}</span>
        {isOpen ? (
          <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px] max-h-[60vh] overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                language === lang.code 
                  ? 'bg-amber-50 text-amber-700 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`language-option-${lang.code}`}
            >
              <span className="text-base">{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FloatingLanguageSelector;
